import React, { useMemo, useState } from 'react';
import { Card, Col, Input, Row, Space, Tag, Typography, Segmented } from 'antd';
import { Link } from 'react-router-dom';
import { FileTextOutlined, DashboardOutlined, BarChartOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import {
  STANDARD_REPORTS,
  filterReportsByPermissions,
} from '@/reports/registry/standardReports';
import { REPORT_MODULE_LABELS, type ReportModuleKey, type ReportDefinition } from '@/reports/types';

const { Title, Text } = Typography;

const FAVORITES_KEY = 'ordan.report.favorites';

function loadFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveFavorites(ids: string[]) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
}

const kindIcon = (kind: ReportDefinition['kind']) => {
  if (kind === 'dashboard') return <DashboardOutlined />;
  if (kind === 'legacy') return <BarChartOutlined />;
  return <FileTextOutlined />;
};

export const ReportsCatalogPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const [q, setQ] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [favorites, setFavorites] = useState<string[]>(() => loadFavorites());

  const reports = useMemo(() => {
    let rows = filterReportsByPermissions(
      STANDARD_REPORTS,
      user?.permissions,
      user?.is_super_admin
    );
    if (moduleFilter !== 'all') {
      rows = rows.filter((r) => r.module === moduleFilter);
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.title.toLowerCase().includes(needle) ||
          r.description.toLowerCase().includes(needle) ||
          r.id.toLowerCase().includes(needle)
      );
    }
    return rows;
  }, [user, q, moduleFilter]);

  const modules = useMemo(() => {
    const set = new Set(STANDARD_REPORTS.map((r) => r.module));
    return Array.from(set);
  }, []);

  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      saveFavorites(next);
      return next;
    });
  };

  const favoriteReports = reports.filter((r) => favorites.includes(r.id));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 8 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        Reports
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Standard registers and financial reports across all modules. Dashboards stay under Analytics /
        Owner Intelligence.
      </Text>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
        <Input.Search
          allowClear
          placeholder="Search reports…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 420 }}
        />
        <Segmented
          value={moduleFilter}
          onChange={(v) => setModuleFilter(String(v))}
          options={[
            { label: 'All', value: 'all' },
            ...modules.map((m) => ({
              label: REPORT_MODULE_LABELS[m as ReportModuleKey] || m,
              value: m,
            })),
          ]}
        />
      </Space>

      {favoriteReports.length > 0 && (
        <>
          <Title level={5}>Favorites</Title>
          <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
            {favoriteReports.map((r) => (
              <Col xs={24} md={12} lg={8} key={`fav-${r.id}`}>
                <ReportCard report={r} favorite onToggleFavorite={() => toggleFavorite(r.id)} />
              </Col>
            ))}
          </Row>
        </>
      )}

      <Title level={5}>Catalog ({reports.length})</Title>
      <Row gutter={[12, 12]}>
        {reports.map((r) => (
          <Col xs={24} md={12} lg={8} key={r.id}>
            <ReportCard
              report={r}
              favorite={favorites.includes(r.id)}
              onToggleFavorite={() => toggleFavorite(r.id)}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

function ReportCard({
  report,
  favorite,
  onToggleFavorite,
}: {
  report: ReportDefinition;
  favorite: boolean;
  onToggleFavorite: () => void;
}) {
  return (
    <Card
      size="small"
      hoverable
      title={
        <Space>
          {kindIcon(report.kind)}
          <Link to={report.route} style={{ color: 'inherit' }}>
            {report.title}
          </Link>
        </Space>
      }
      extra={
        <button
          type="button"
          onClick={onToggleFavorite}
          aria-label={favorite ? 'Unfavorite' : 'Favorite'}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16 }}
        >
          {favorite ? '★' : '☆'}
        </button>
      }
    >
      <Text type="secondary" style={{ fontSize: 13 }}>
        {report.description}
      </Text>
      <div style={{ marginTop: 8 }}>
        <Tag>{REPORT_MODULE_LABELS[report.module]}</Tag>
        <Tag>{report.kind}</Tag>
      </div>
    </Card>
  );
}

export default ReportsCatalogPage;
